import React from "react";

/** 出错后显示的元素类型 */
type FallbackElement = React.ReactElement<
  unknown,
  string | React.FC | typeof React.Component
> | null;

declare function FallbackRender(props: FallbackProps): FallbackElement;

interface FallbackProps {
  error: Error | true;
  errorInfo: React.ErrorInfo | null;
  resetErrorBoundary: () => void;
}

interface ErrorBoundaryProps {
  copyStr?: string;
  fallback?: FallbackElement;
  FallbackComponent?: React.ComponentType<FallbackProps>;
  fallbackRender?: typeof FallbackRender;
  onError?: (error: Error, info?: string) => void;
  onReset?: () => void;
}

interface ErrorBoundaryState {
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/** 初始状态 */
const initialState: ErrorBoundaryState = {
  error: null,
  errorInfo: null
};

export default class ErrorBoundary extends React.Component<
  React.PropsWithChildren<ErrorBoundaryProps>,
  ErrorBoundaryState
> {
  state = initialState;

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      errorInfo
    });
    if (this.props.onError) {
      this.props.onError(error, errorInfo.componentStack);
    }
  }

  /**
   * 重置组件状态
   */
  reset = () => {
    this.setState(initialState);
  };

  /**
   * 执行自定义重置逻辑，并重置组件状态
   */
  resetErrorBoundary = () => {
    if (this.props.onReset) {
      this.props.onReset();
    }
    this.reset();
  };

  render() {
    const {
      fallback,
      FallbackComponent,
      fallbackRender,
      onReset,
      onError,
      ...restProps
    } = this.props;
    const { error, errorInfo } = this.state;

    // 多种 fallback 的判断
    if (error !== null) {
      const fallbackProps: FallbackProps = {
        error,
        errorInfo,
        ...restProps,
        resetErrorBoundary: this.resetErrorBoundary
      };

      // 判断 fallback 是否为合法的 Element
      if (React.isValidElement(fallback)) {
        return fallback;
      }

      // 判断 render 是否为函数
      if (typeof fallbackRender === "function") {
        return (fallbackRender as typeof FallbackRender)(fallbackProps);
      }

      // 判断是否存在 FallbackComponent
      if (FallbackComponent) {
        return <FallbackComponent {...fallbackProps} />;
      }

      throw new Error(
        "ErrorBoundary 组件需要传入 fallback, fallbackRender, FallbackComponent 其中一个"
      );
    }

    return this.props.children;
  }
}
